import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import Guid from "_shared/guids/guid";
import LoadingButton from "_shared/_views/loading/loading-button";
import Loader from "_shared/_views/loading/loader";
import { useGetDocumentsByIdsQuery, useGetDocumentsQuery } from "documents/_api/document-api";
import DocumentQuery, {
  DocumentFilterParameters,
  DocumentOrderField,
  DocumentOrderParameters,
} from "documents/_api/queries/document-query";
import Document from "documents/document";
import "permissions/__extensions/users/user-extensions";
import useSessionUser from "permissions/session/use-session-user";
import {
  isUnauthorizedClientProposalTemplateError,
  useGetClientProposalTemplateQuery,
  useUpdateClientProposalTemplateMutation,
} from "work/client-proposal-templates/_api/client-proposal-template-api";
import {
  describeDiscountProblem,
  hasUnsavedChanges,
  toFormState,
  toUpdateRequest,
  type ClientProposalTemplateFormState,
} from "work/client-proposal-templates/_views/client-proposal-template-form";
import TemplateChoiceField, {
  TemplateChoiceMultiField,
} from "work/client-proposal-templates/_views/template-choice-field";
import { buildChoices, type TemplateChoice } from "work/client-proposal-templates/_views/template-choices";
import {
  useGetFeeScheduleTemplatesByIdsQuery,
  useGetFeeScheduleTemplatesQuery,
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import { FeeScheduleTemplateFilterParameters } from "work/fee-schedule-templates/_api/queries/contracts/filtering/fee-schedule-template-filter-parameters";
import { FeeScheduleTemplateOrderField } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-field";
import { FeeScheduleTemplateOrderParameters } from "work/fee-schedule-templates/_api/queries/contracts/ordering/fee-schedule-template-order-parameters";
import FeeScheduleTemplateQuery from "work/fee-schedule-templates/_api/queries/contracts/fee-schedule-template-query";
import { useGetTeamTemplateByIdQuery, useGetTeamTemplatesQuery } from "work/team-templates/_api/team-template-api";
import { TeamTemplateOrderField } from "work/team-templates/_api/queries/team-template-order-field";
import TeamTemplateQuery, {
  TeamTemplateFilterParameters,
  TeamTemplateOrderParameters,
} from "work/team-templates/_api/queries/team-template-query";
import { TeamTemplateParty } from "work/team-templates/team-template-party";

// #region Styles
// Bounded to the shell's content section, which clips with `overflow: hidden` and does not scroll
// itself. Without a height limit the paper grows past the viewport, its own `overflow: auto` never
// engages, and everything below the fold — the Save button included — cannot be reached.
const PageContainer = styled(Paper)(({ theme }) => ({
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  margin: theme.spacing(1),
  maxHeight: `calc(100% - ${theme.spacing(2)})`,
  overflow: "auto",
  padding: theme.spacing(2),
}));
const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(0.5),
}));
const PageIntroduction = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
  maxWidth: "48rem",
}));
const Fields = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  maxWidth: "48rem",
}));
const SectionHeading = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));
const StatusAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  maxWidth: "48rem",
}));
const Actions = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(3),
}));
// #endregion

const INTERFACE_NAME = "ClientProposalTemplatesPage";

/**
 * How many of each list the selectors are given.
 *
 * A page rather than everything, because every list endpoint here paginates. It is generous enough
 * that an organization's whole library of templates is normally in hand, and a choice made outside
 * it is still named: the saved ids are resolved separately and folded in (see `buildChoices`).
 */
const CHOICE_PAGE_SIZE = 100;

/**
 * Which of this organization's templates its proposals to newly rolled-out firms are built from.
 *
 * The client's own half of automatic proposal creation. Everything the roll-out needs to assemble a
 * draft was buildable through the API alone until this page existed, which in practice meant the
 * feature was off for everybody (issue #5214).
 *
 * **There is no id on this page, and no organization picker.** The API resolves the record from the
 * bearer token, so the page configures the organization the reader signed in as and can reach no
 * other. That is the same rule the firm onboarding board applies, arrived at from the other
 * direction — see `client-proposal-template-api.ts`.
 */
export default function ClientProposalTemplatesPage() {
  const user = useSessionUser();
  const legalEntityId = user?.legalEntityId;

  const configurationQuery = useGetClientProposalTemplateQuery();
  const [updateConfiguration, updateState] = useUpdateClientProposalTemplateMutation();
  const configuration = configurationQuery.data;

  /**
   * The edit in hand, or null while the page is showing exactly what the API returned.
   *
   * Null rather than a copy seeded in an effect. A `useEffect` that pushed the loaded configuration
   * into state would be a `set-state-in-effect` bailout, and it would also race a refetch: the
   * saved record arriving second would be written over the reader's typing. Derived below instead,
   * and put back to null on a successful save so the fresh record becomes what is shown.
   */
  const [edit, setEdit] = useState<ClientProposalTemplateFormState | null>(null);
  const state = edit ?? toFormState(configuration);

  const feeScheduleTemplatesQuery = useGetFeeScheduleTemplatesQuery(
    legalEntityId
      ? {
        query: new FeeScheduleTemplateQuery({
          filterParameters: new FeeScheduleTemplateFilterParameters({ entityId: legalEntityId }),
          orderParameters: new FeeScheduleTemplateOrderParameters({
            orderBy: FeeScheduleTemplateOrderField.Name,
            orderDir: OrderDirection.Ascending,
          }),
          paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: CHOICE_PAGE_SIZE }),
        }),
        interfaceName: INTERFACE_NAME,
      }
      : skipToken,
  );
  const savedFeeScheduleTemplateQuery = useGetFeeScheduleTemplatesByIdsQuery(
    state.feeScheduleTemplateId ? { feeScheduleTemplateIds: [state.feeScheduleTemplateId] } : skipToken,
  );

  const teamTemplatesQuery = useGetTeamTemplatesQuery({
    query: new TeamTemplateQuery({
      // The client's own rosters, asked for rather than filtered out of the answer: the server
      // defaults this filter to Vendor, so a request that named no party would come back holding
      // the firms' team templates instead.
      filterParameters: new TeamTemplateFilterParameters({ party: TeamTemplateParty.Client }),
      orderParameters: new TeamTemplateOrderParameters({
        orderBy: TeamTemplateOrderField.Name,
        orderDir: OrderDirection.Ascending,
      }),
      paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: CHOICE_PAGE_SIZE }),
    }),
    interfaceName: INTERFACE_NAME,
  });
  const savedTeamTemplateQuery = useGetTeamTemplateByIdQuery(
    state.teamTemplateId ? { templateId: state.teamTemplateId } : skipToken,
  );

  const conflictsDocumentsQuery = useGetDocumentsQuery({
    query: documentTemplateQuery("conflictstemplate"),
    interfaceName: INTERFACE_NAME,
  });
  const policyDocumentsQuery = useGetDocumentsQuery({
    query: documentTemplateQuery("policytemplate"),
    interfaceName: INTERFACE_NAME,
  });
  // Freshly allocated on every render and deliberately not memoized: RTK Query keys a cache entry
  // on the serialized argument, so a new array with the same ids is the same subscription. A
  // `useMemo` here would list a dependency that is itself rebuilt each render, which is the shape
  // that works only while the component compiles.
  const savedDocumentIds = [
    ...new Set([state.conflictsTemplateDocumentId, ...state.policyTemplateDocumentIds].filter(Boolean)),
  ];
  const savedDocumentsQuery = useGetDocumentsByIdsQuery(
    savedDocumentIds.length > 0 ? { documentIds: savedDocumentIds } : skipToken,
  );

  const savedDocumentChoices = (savedDocumentsQuery.data ?? [])
    .filter((result): result is Document => result instanceof Document)
    .map((document) => ({ id: document.id.value, name: document.name.value }));

  const feeScheduleChoices = buildChoices(
    (feeScheduleTemplatesQuery.data?.data ?? []).map(toIdAndName),
    (savedFeeScheduleTemplateQuery.data ?? []).map(toIdAndName),
  );
  const teamChoices = buildChoices(
    (teamTemplatesQuery.data?.data ?? []).map(toIdAndName),
    savedTeamTemplateQuery.data ? [toIdAndName(savedTeamTemplateQuery.data)] : [],
  );
  const conflictsChoices = buildChoices(
    (conflictsDocumentsQuery.data?.data ?? []).map(toDocumentChoice),
    savedDocumentChoices,
  );
  const policyChoices = buildChoices(
    (policyDocumentsQuery.data?.data ?? []).map(toDocumentChoice),
    savedDocumentChoices,
  );

  const discountProblem = describeDiscountProblem(state.discountPercent);
  const isDirty = hasUnsavedChanges(state, configuration);
  const canEdit = user?.canConfigureClientProposalTemplates() === true;
  const isBusy = updateState.isLoading || configurationQuery.isFetching;

  function change(fields: Partial<ClientProposalTemplateFormState>) {
    setEdit({ ...state, ...fields });
  }

  async function handleSave() {
    const saved = await saveClientProposalTemplate(updateConfiguration, toUpdateRequest(state));
    if (saved) setEdit(null);
  }

  if (!user) return <Loader text="Loading proposal templates..." />;

  if (configurationQuery.isLoading) return <Loader text="Loading proposal templates..." />;

  if (configurationQuery.isError) {
    return (
      <PageContainer>
        <PageTitle variant="h5">Proposal templates</PageTitle>
        <StatusAlert severity="error">
          {isUnauthorizedClientProposalTemplateError(configurationQuery.error)
            ? "You do not have permission to see your organization's proposal terms."
            : (getErrorMessageFromUnknown(configurationQuery.error) ??
              "Your organization's proposal templates could not be loaded.")}
        </StatusAlert>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle variant="h5">Proposal templates</PageTitle>
      <PageIntroduction variant="body2">
        When a firm you have invited creates its account, Attorney Hub can assemble that firm&apos;s
        proposal from the templates you choose here. It stops at a draft: you still open it, staff
        the hourly categories and press send.
      </PageIntroduction>

      {configuration?.gapReason && (
        // The server's sentence rather than a copy of it, and in the words the roll-out records
        // against the firm — so the board and this page cannot explain the same stall differently.
        <StatusAlert severity="info">
          <AlertTitle>No drafts are being built</AlertTitle>
          {configuration.gapReason}
        </StatusAlert>
      )}
      {configuration?.isReadyForAutoCreation && (
        <StatusAlert severity="success">
          Drafts are being built for firms that finish creating their accounts.
        </StatusAlert>
      )}
      {!canEdit && (
        <StatusAlert severity="warning">
          You can see these terms but not change them. Setting them needs entity-wide access to
          proposals and to discounts.
        </StatusAlert>
      )}

      <Fields>
        <SectionHeading variant="subtitle1">What the proposal is built from</SectionHeading>
        <TemplateChoiceField
          label="Fee schedule template"
          helperText="The rates the proposal offers. Without one, nothing is built."
          choices={feeScheduleChoices}
          disabled={!canEdit || isBusy}
          loading={feeScheduleTemplatesQuery.isFetching}
          value={state.feeScheduleTemplateId}
          onChange={(id) => change({ feeScheduleTemplateId: id })}
        />
        <TemplateChoiceField
          label="Client team template"
          helperText="Who at your organization leads the matter. Without one, nothing is built."
          choices={teamChoices}
          disabled={!canEdit || isBusy}
          loading={teamTemplatesQuery.isFetching}
          value={state.teamTemplateId}
          onChange={(id) => change({ teamTemplateId: id })}
        />
        <TemplateChoiceField
          label="Conflicts template"
          helperText="Copied onto each proposal. Optional."
          choices={conflictsChoices}
          disabled={!canEdit || isBusy}
          loading={conflictsDocumentsQuery.isFetching}
          value={state.conflictsTemplateDocumentId}
          onChange={(id) => change({ conflictsTemplateDocumentId: id })}
        />
        <TemplateChoiceMultiField
          label="Policy templates"
          helperText="Copied onto each proposal, in the order you arrange them. Optional."
          choices={policyChoices}
          disabled={!canEdit || isBusy}
          loading={policyDocumentsQuery.isFetching}
          value={state.policyTemplateDocumentIds}
          onChange={(ids) => change({ policyTemplateDocumentIds: ids })}
        />

        <Divider />
        <SectionHeading variant="subtitle1">The terms it goes out on</SectionHeading>
        <TextField
          label="Rate discount (%)"
          type="number"
          value={state.discountPercent}
          disabled={!canEdit || isBusy}
          error={discountProblem !== undefined}
          helperText={discountProblem ?? "The discount agreed with you, applied to every firm you onboard."}
          onChange={(event) => change({ discountPercent: event.target.value })}
        />
        <FormControlLabel
          control={
            <Switch
              checked={state.negotiable}
              disabled={!canEdit || isBusy}
              onChange={(event) => change({ negotiable: event.target.checked })}
            />
          }
          label="The firm may counter these terms"
        />
        <FormControlLabel
          control={
            <Switch
              checked={state.conflictsCheckWaived}
              disabled={!canEdit || isBusy}
              onChange={(event) => change({ conflictsCheckWaived: event.target.checked })}
            />
          }
          label="Waive the conflicts check"
        />

        <Divider />
        <SectionHeading variant="subtitle1">Automatic creation</SectionHeading>
        <FormControlLabel
          control={
            <Switch
              checked={state.autoCreateEnabled}
              disabled={!canEdit || isBusy}
              onChange={(event) => change({ autoCreateEnabled: event.target.checked })}
            />
          }
          label="Build a draft proposal for each firm that creates its account"
        />
      </Fields>

      <Actions>
        <LoadingButton
          variant="contained"
          loading={updateState.isLoading}
          disabled={!canEdit || !isDirty || discountProblem !== undefined}
          onClick={() => void handleSave()}
        >
          Save
        </LoadingButton>
      </Actions>
    </PageContainer>
  );
}

/** Both document lists are the same query with a different context. */
function documentTemplateQuery(context: "conflictstemplate" | "policytemplate"): DocumentQuery {
  return new DocumentQuery({
    filterParameters: new DocumentFilterParameters({ contexts: [context], ownershipScope: "entity" }),
    orderParameters: new DocumentOrderParameters({
      orderBy: DocumentOrderField.Name,
      orderDir: OrderDirection.Ascending,
    }),
    paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: CHOICE_PAGE_SIZE }),
  });
}

function toIdAndName(template: { id?: string | Guid | null; name: string }): TemplateChoice {
  const id = template.id instanceof Guid ? template.id.value : (template.id ?? "");
  return { id, name: template.name };
}

function toDocumentChoice(document: Document): TemplateChoice {
  return { id: document.id.value, name: document.name.value };
}

/**
 * Sends one save and reports the record it stored, or undefined.
 *
 * At module scope because it holds a `try`/`catch` around an `await` — a component containing one
 * is emitted uncompiled in its entirety, memoization and all (see app/CLAUDE.md).
 */
async function saveClientProposalTemplate(
  update: ReturnType<typeof useUpdateClientProposalTemplateMutation>[0],
  request: ReturnType<typeof toUpdateRequest>,
) {
  try {
    const saved = await update(request).unwrap();
    enqueueSnackbar("Proposal templates saved", { variant: "success" });
    return saved;
  } catch (error) {
    console.error("Failed to save the proposal template configuration", error);
    enqueueSnackbar(
      isUnauthorizedClientProposalTemplateError(error)
        ? "You do not have permission to change your organization's proposal terms."
        : (getErrorMessageFromUnknown(error) ?? "Failed to save proposal templates. Please try again."),
      { variant: "error" },
    );
    return undefined;
  }
}
